import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import TextLink from "@8x8/oxygen-text-link";

import { filterTableMutations } from "@/react/components/table/filterTableMutations";
import TableComponent from "@/react/components/table/TableComponent";
import { useTable } from "@/react/components/table/useTable";
import { CustomerCard } from "@/react/features/customer/card";
import { customerIdSelector } from "@/react/features/customer/customerSlice";
import { InnerContainer } from "@/react/features/support-tools/common/styled";
import { useErrorNotification } from "@/react/notifications";
import TableWrapper from "@/react/features/styled/TableWrapper";
import PageLayout from "@/react/features/styled/PageLayout";
import PageTitle from "@/react/components/PageTitle";

import { useGetCSRQuery } from "./api";
import columnDefinition from "./columnDefinition";
import schema from "./schema";

const CSR_HELP_URL =
  "https://support-portal.8x8.com/viewArticle.html?d=862d9429-0643-4b63-950b-f76e9eba2e4f&c=1_40_65_68_7594_&hl=en";

const columns = columnDefinition();

const Title = styled(PageTitle)`
  margin: 0 0 8px;
`;

const Description = styled.div`
  margin: 0 0 8px;
`;

const CustomerServiceRecordPage = () => {
  const cid = useSelector(customerIdSelector);

  const { data: records, isFetching, error, refetch } = useGetCSRQuery(cid);

  // This is so that we auto-reload the table when the customer changes
  useEffect(() => {
    if (refetch) {
      refetch();
    }
  }, [cid]);

  useErrorNotification(error);

  const { table, filters, state, mutations } = useTable(
    records,
    columns,
    schema
  );
  const noMutations = useMemo(
    () => filterTableMutations(mutations),
    [mutations]
  );

  return (
    <PageLayout>
      <CustomerCard hasPbxSelector={true} />
      <Title>Customer Service Record</Title>
      <InnerContainer>
        <Description>
          This tool helps to generate Customer Service Records (CSR's), which
          hold all the information that a customer may need to port their number
          out of 8x8 service.
          <br />
          For more information, refer
          <TextLink
            underline="hover"
            rel="noopener"
            target="_blank"
            href={CSR_HELP_URL}
          >
            here
          </TextLink>
        </Description>
        <TableWrapper>
          <TableComponent
            title="Records"
            schema={schema}
            isLoading={isFetching}
            onRefresh={refetch}
            table={table}
            filters={filters}
            mutations={noMutations}
            state={state}
          />
        </TableWrapper>
      </InnerContainer>
    </PageLayout>
  );
};

export default CustomerServiceRecordPage;
